import Service, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { QueryParamsSource } from '@warp-drive/core-types/params';
// @ts-expect-error We don't have types, but we also want to replace the addon
import { storageFor } from 'ember-local-storage';

import type BreadcrumbService from './breadcrumb';
import type CurrentUserService from './current-user';

type FilterValue = string | number | boolean | null | undefined;
type FilterParams = Record<string, FilterValue>;
type FilterConfig = {
  filters?: FilterParams;
  sort?: string;
  pageSize?: number;
};

export default class FilterService extends Service {
  // Services
  @service declare breadcrumb: BreadcrumbService;
  @service declare currentUser: CurrentUserService;
  @storageFor('table-config') tableConfigs: TODO;

  // Defaults
  @tracked _globalArtist = null;
  @tracked globalUser = null;
  @tracked globalChannel = null;

  // Getter and setter
  get globalArtist() {
    if (this.currentUser.hasOneArtist) {
      return this.currentUser.artists[0];
    }

    return this._globalArtist;
  }

  set globalArtist(value) {
    this._globalArtist = value;
  }

  // Functions
  getQueryObject(modelName: string, queryParams: FilterParams = {}) {
    const queryObject: QueryParamsSource = {};
    // @ts-expect-error BreadcrumbService: The breadcrumb service don't have types
    const routeData = this.breadcrumb.currentRoute?.routeData;
    const config = (this.tableConfigs.get(modelName) || {}) as FilterConfig;

    // Filter
    Object.keys(queryParams).forEach((key) => {
      if (queryParams[key] === undefined) {
        delete queryParams[key];
      }
    });

    const filterObject: FilterParams = {
      ...(config.filters ?? {}),
      ...queryParams,
    };

    if (this.globalArtist) {
      filterObject['artist'] = this.globalArtist.get('id');
    }

    Object.entries(filterObject).forEach(([key, value]) => {
      if (value !== undefined) {
        queryObject[`filter[${key}]`] = value;
      }
    });

    // Search
    const searchString = routeData
      ? routeData.get(`filter-searchString-${modelName}`)
      : null;

    if (searchString) {
      queryObject['search[value]'] = searchString.trim();
    }

    // Sort
    let sortProperty = config.sort || '-cmsModified';
    const isAsc = sortProperty[0] !== '-';
    const sortOrder = isAsc ? 'ASC' : 'DESC';
    sortProperty = sortProperty?.replace('-', '');
    queryObject[`sort[${sortProperty}]`] = sortOrder;

    // Pagination
    const pageSize = config.pageSize || 30;
    const page = routeData ? routeData.get(`filter-page-${modelName}`) : 1;
    const offset = pageSize * page - pageSize;

    queryObject['limit'] = pageSize;
    queryObject['offset'] = offset;

    // Overwrites
    if (modelName === 'user-role') {
      Object.keys(queryObject).forEach((key) => {
        if (key.startsWith('filter[') || key.startsWith('sort[')) {
          delete queryObject[key];
        }
      });
    }

    return queryObject;
  }
}
